import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowUpRight, Paperclip, Phone, X } from "@phosphor-icons/react";
import Seo from "@/components/Seo";
import { Reveal } from "@/components/Motion";
import { Chapter, PageHero } from "@/components/Sections";
import { API, COMPANY, OPGAVE_TYPER, OPSTART_OPTIONS } from "@/lib/content";

const FIELD =
  "w-full bg-transparent border-b border-ink/20 focus:border-signal outline-none py-3 text-base placeholder:text-ink/30 transition-colors duration-300";
const LABEL = "block text-[10px] uppercase tracking-[0.2em] text-soot mb-1";

const Field = ({ label, children, testId }) => (
  <div data-testid={testId}>
    <label className={LABEL}>{label}</label>
    {children}
  </div>
);

const Contact = () => {
  const [form, setForm] = useState({
    virksomhedsnavn: "",
    kontaktperson: "",
    cvr: "",
    telefon: "",
    email: "",
    projektadresse: "",
    opgavetype: "",
    opstart: "",
    beskrivelse: "",
  });
  const [files, setFiles] = useState([]);
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (sending) return;
    setSending(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      files.forEach((f) => fd.append("filer", f));
      await axios.post(`${API}/henvendelser`, fd);
      toast.success("Tak for henvendelsen — vi vender tilbage hurtigst muligt.");
      setForm({
        virksomhedsnavn: "",
        kontaktperson: "",
        cvr: "",
        telefon: "",
        email: "",
        projektadresse: "",
        opgavetype: "",
        opstart: "",
        beskrivelse: "",
      });
      setFiles([]);
    } catch (err) {
      toast.error("Noget gik galt. Prøv igen, eller ring til os direkte.");
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <Seo
        title="Kontakt — Indhent tilbud på malerarbejde til erhverv | Al Maler Mester ApS"
        description="Kontakt Al Maler Mester ApS for et uforpligtende tilbud på malerarbejde til erhverv. Ring 91 65 44 29 eller udfyld formularen med jeres projekt."
      />
      <PageHero
        overline="Kontakt"
        title={
          <>
            Lad os tage en snak om jeres{" "}
            <span className="font-editorial italic font-medium text-signal">næste projekt</span>
          </>
        }
        lead="Søger jeres virksomhed en professionel maler eller samarbejdspartner til en kommende opgave? Kontakt Al Maler Mester ApS for en uforpligtende dialog om projektet."
      />

      <section className="max-w-[1440px] mx-auto px-5 md:px-10 pb-24 md:pb-36">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-14">
          <Reveal className="lg:col-span-4">
            <div className="bg-ink text-bone p-8 md:p-10 grain lg:sticky lg:top-28" data-testid="contact-company-card">
              <p className="text-[10px] uppercase tracking-[0.25em] text-bone/40 mb-8">Al Maler Mester ApS</p>
              <dl className="space-y-5 text-sm">
                <div className="border-b border-bone/10 pb-4">
                  <dt className="text-bone/50 mb-1">CVR</dt>
                  <dd className="font-medium">{COMPANY.cvr}</dd>
                </div>
                <div className="border-b border-bone/10 pb-4">
                  <dt className="text-bone/50 mb-1">Adresse</dt>
                  <dd className="font-medium">
                    {COMPANY.street}
                    <br />
                    {COMPANY.city}
                  </dd>
                </div>
                <div className="border-b border-bone/10 pb-4">
                  <dt className="text-bone/50 mb-1">Telefon</dt>
                  <dd>
                    <a href={COMPANY.phoneHref} data-testid="contact-phone-link" className="font-medium hover:text-signal transition-colors duration-300">
                      {COMPANY.phone}
                    </a>
                  </dd>
                </div>
                <div>
                  <dt className="text-bone/50 mb-1">Kundetype</dt>
                  <dd className="font-medium">Udelukkende erhverv (B2B)</dd>
                </div>
              </dl>
              <a
                href={COMPANY.phoneHref}
                data-testid="contact-call-button"
                className="mt-10 flex items-center justify-center gap-2 bg-signal text-bone font-semibold px-6 py-4 text-sm hover:bg-bone hover:text-ink transition-colors duration-300"
              >
                <Phone size={16} weight="bold" /> Ring {COMPANY.phoneDisplay}
              </a>
            </div>
          </Reveal>

          <Reveal delay={0.12} className="lg:col-span-8">
            <Chapter nr="01" label="Indhent tilbud" />
            <form data-testid="quote-form" onSubmit={onSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-8">
              <Field label="Virksomhedsnavn *" testId="field-company">
                <input required className={FIELD} data-testid="input-company" value={form.virksomhedsnavn} onChange={set("virksomhedsnavn")} placeholder="Fx Byggefirma ApS" />
              </Field>
              <Field label="Kontaktperson *" testId="field-contact-person">
                <input required className={FIELD} data-testid="input-contact-person" value={form.kontaktperson} onChange={set("kontaktperson")} placeholder="Fulde navn" />
              </Field>
              <Field label="CVR-nr. (valgfrit)" testId="field-cvr">
                <input className={FIELD} data-testid="input-cvr" value={form.cvr} onChange={set("cvr")} placeholder="8 cifre" />
              </Field>
              <Field label="Telefon *" testId="field-phone">
                <input required type="tel" className={FIELD} data-testid="input-phone" value={form.telefon} onChange={set("telefon")} placeholder="+45" />
              </Field>
              <Field label="E-mail *" testId="field-email">
                <input required type="email" className={FIELD} data-testid="input-email" value={form.email} onChange={set("email")} placeholder="navn@virksomhed.dk" />
              </Field>
              <Field label="Projektadresse *" testId="field-address">
                <input required className={FIELD} data-testid="input-address" value={form.projektadresse} onChange={set("projektadresse")} placeholder="Vej, postnr. og by" />
              </Field>
              <Field label="Type af opgave *" testId="field-type">
                <select required className={`${FIELD} bg-bone`} data-testid="input-type" value={form.opgavetype} onChange={set("opgavetype")}>
                  <option value="" disabled>Vælg opgavetype</option>
                  {OPGAVE_TYPER.map((t) => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </select>
              </Field>
              <Field label="Forventet opstart *" testId="field-start">
                <select required className={`${FIELD} bg-bone`} data-testid="input-start" value={form.opstart} onChange={set("opstart")}>
                  <option value="" disabled>Vælg tidspunkt</option>
                  {OPSTART_OPTIONS.map((t) => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </select>
              </Field>
              <div className="md:col-span-2" data-testid="field-description">
                <label className={LABEL}>Beskrivelse af opgaven *</label>
                <textarea
                  required
                  rows={5}
                  className={`${FIELD} resize-none`}
                  data-testid="input-description"
                  value={form.beskrivelse}
                  onChange={set("beskrivelse")}
                  placeholder="Beskriv kort omfang, overflader, tidsplan og eventuelle særlige krav."
                />
              </div>
              <div className="md:col-span-2" data-testid="field-files">
                <label className={LABEL}>Upload billeder/dokumenter</label>
                <label
                  data-testid="file-upload-zone"
                  className="flex items-center justify-center gap-3 border border-dashed border-ink/25 hover:border-signal px-6 py-8 cursor-pointer transition-colors duration-300 text-sm text-soot"
                >
                  <Paperclip size={18} />
                  {files.length ? `${files.length} fil(er) valgt` : "Vælg filer (billeder, PDF m.m. — maks. 15 MB pr. fil)"}
                  <input
                    type="file"
                    multiple
                    className="hidden"
                    data-testid="input-files"
                    accept=".jpg,.jpeg,.png,.webp,.heic,.pdf,.doc,.docx"
                    onChange={(e) => setFiles(Array.from(e.target.files || []))}
                  />
                </label>
                {files.length > 0 && (
                  <ul className="mt-3 space-y-1.5">
                    {files.map((f, i) => (
                      <li key={i} className="flex items-center justify-between text-xs text-soot">
                        <span className="truncate">{f.name}</span>
                        <button
                          type="button"
                          data-testid={`file-remove-${i}`}
                          onClick={() => setFiles(files.filter((_, j) => j !== i))}
                          className="ml-3 hover:text-signal transition-colors"
                          aria-label="Fjern fil"
                        >
                          <X size={14} />
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <div className="md:col-span-2 pt-4">
                <button
                  type="submit"
                  disabled={sending}
                  data-testid="quote-submit-button"
                  className="group flex items-center justify-center gap-2 bg-signal text-bone font-semibold px-10 py-4 text-sm hover:bg-ink transition-colors duration-300 disabled:opacity-60 w-full sm:w-auto"
                >
                  {sending ? "Sender…" : "Indhent tilbud"}
                  <ArrowUpRight size={16} weight="bold" className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </button>
                <p className="mt-4 text-xs text-soot">
                  Ved afsendelse accepterer I vores privatlivspolitik. Vi bruger kun jeres oplysninger til at behandle henvendelsen.
                </p>
              </div>
            </form>
          </Reveal>
        </div>
      </section>
    </>
  );
};

export default Contact;
